import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { selectiveRealtimeManager } from '@/lib/realtime';
import { useAuthStore } from '@/lib/auth';

interface PageContextType {
  currentPage: string;
  updatePageContext: (page: string) => void;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

export const usePageContext = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within a PageContextProvider');
  }
  return context;
};

interface PageContextProviderProps {
  children: React.ReactNode;
}

export const PageContextProvider: React.FC<PageContextProviderProps> = ({ children }) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string>('');
  const user = useAuthStore((state) => state.user);

  // Extract page name from pathname
  const getPageFromPath = (pathname: string): string => {
    const path = pathname.split('/')[1]; // Get first segment after /
    
    // Map routes to page names
    switch (path) {
      case '':
        return 'dashboard';
      case 'doctor-dashboard':
        return 'doctor-dashboard';
      case 'e-prescription':
        return 'e-prescription';
      case 'queue':
        return 'queue';
      case 'registration':
        return 'registration';
      case 'analytics':
        return 'analytics';
      case 'history':
        return 'history';
      case 'settings':
        return 'settings';
      case 'users':
        return 'users';
      case 'followups':
        return 'followups';
      default:
        return 'other';
    }
  };

  const updatePageContext = (page: string) => {
    setCurrentPage(page);
    // Update selective subscriptions with new context
    selectiveRealtimeManager.updateContext(user?.role || null, page);
  };

  useEffect(() => {
    const page = getPageFromPath(location.pathname);
    updatePageContext(page);
  }, [location.pathname, user?.role]);

  const value: PageContextType = {
    currentPage,
    updatePageContext
  };

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}; 